import { create } from "zustand";

/**
 * Live overrides for the product studio, for a development panel.
 *
 * Same arrangement as {@link useAmbientOcclusionTuning}: the panel is DOM
 * outside the canvas, and this store is how it reaches `StandardEnvironment`.
 * Every field is an override, so the state a fresh session starts in is "no
 * overrides" and the production path renders exactly the committed constants;
 * nothing has to opt in.
 */
export interface EnvironmentStudioOverrides {
  /** URL of an equirectangular HDRI to light with instead of the shipped one. */
  hdri: string | null;
  /** Yaw of the photographed studio in degrees, or the shipped value. */
  rotationDeg: number | null;
  /** Whether the composited softboxes are rendered in front of the photograph. */
  softboxes: boolean | null;
  /** Whether the studio turns with the camera, or stays fixed in the world. */
  followCamera: boolean | null;
}

interface EnvironmentStudioState extends EnvironmentStudioOverrides {
  set: (overrides: Partial<EnvironmentStudioOverrides>) => void;
  reset: () => void;
}

const NO_OVERRIDES: EnvironmentStudioOverrides = {
  hdri: null,
  rotationDeg: null,
  softboxes: null,
  followCamera: null,
};

export const useEnvironmentStudio = create<EnvironmentStudioState>((set) => ({
  ...NO_OVERRIDES,
  set: (overrides) => set(overrides),
  reset: () => set(NO_OVERRIDES),
}));
